import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const panelVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.25, type: "spring", stiffness: 300, damping: 25 },
  },
  exit: { opacity: 0, y: 50, scale: 0.98, transition: { duration: 0.2 } },
};

/**
 * Modal base component
 * - Provides safe outside-click close (only when mousedown starts on overlay)
 * - Supports header/content/footer JSX props for flexible layouts
 */
const Modal = ({
  isOpen,
  onClose,
  header,
  content,
  footer,
  overlayClassName = "",
  panelClassName = "",
  closeOnEsc = true,
  ariaLabel,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeOnEsc, onClose]);

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
          className={`fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm overflow-y-auto flex justify-center items-start p-4 ${overlayClassName}`}
          onMouseDown={handleOverlayMouseDown}
          aria-modal="true"
          role="dialog"
          aria-label={ariaLabel}
        >
          <motion.div
            variants={panelVariants}
            className={panelClassName}
            ref={panelRef}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {header}
            {content}
            {footer}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;


